import React, {useId, useState, useEffect} from "react"
import {
	Box,
	Button,
	Grid,
	TextField,
	Select,
	MenuItem,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	InputLabel,
	CircularProgress,
	Chip,
} from "@mui/material"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import CloseIcon from "@mui/icons-material/Close"

import {useFileUpload, FileUpload} from "../../hooks/useFileUpload"

import {api} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

type FieldType = "textarea" | "text" | "number" | "date" | "boolean" | "select" | "url" | string

type Field = {
	Name: string
	Type: FieldType
	Required: boolean
	Options?: string[]
}

type FieldValue = string | number | boolean

type FieldAnswered = {
	Name: string
	Type: FieldType
	Content: FieldValue
}

type Form = {
	Type: string
	Label: string
	Version: number
	Fields: Field[]
}

type Upload = {
	ID: string
	FileName: string
}

type NoteFormHandle = {
	createNote: () => Promise<string | null>
	hasContent: () => boolean
	isUploading: () => boolean
}

type NoteFormProps = {
	customerID: string
	forms: Form[]
	disabled?: boolean
	onSuccess?: () => void
	onNoteCreated?: (noteID: string) => void
	onUploadingChange?: (uploading: boolean) => void
	attachmentsEnabled: boolean
	size?: "small" | "medium"
	showSubmitButton?: boolean
	submitButtonLabel?: string
	attachButtonLabel?: string
	pending?: boolean
}

const isTextareaField = (field: Field | FieldAnswered) => field.Type === "textarea"
const isNumberField = (field: Field | FieldAnswered) => field.Type === "number"
const isBooleanField = (field: Field | FieldAnswered) => field.Type === "boolean"
const isDateField = (field: Field | FieldAnswered) => field.Type === "date"
const isSelectField = (field: Field | FieldAnswered) => field.Type === "select"
const isURLField = (field: Field | FieldAnswered) => field.Type === "url"

const defaultValueForField = (field: Field): FieldValue => {
	if (isBooleanField(field)) return false
	if (isNumberField(field)) return 0
	return ""
}

const hasFieldContent = (field: Field, value: FieldValue | undefined): boolean => {
	if (value === undefined) return false
	if (isBooleanField(field)) return typeof value === "boolean"
	if (isNumberField(field)) return value !== 0 && value !== "" && !Number.isNaN(value)
	return typeof value === "string" ? value.trim() !== "" : value !== undefined
}

const NoteForm = React.forwardRef<NoteFormHandle, NoteFormProps>(function NoteForm(
	{
		customerID,
		forms,
		disabled = false,
		onSuccess,
		onNoteCreated,
		onUploadingChange,
		attachmentsEnabled,
		size = "medium",
		showSubmitButton = true,
		submitButtonLabel = "ADD NOTE",
		attachButtonLabel = "ATTACH FILES",
		pending,
	},
	ref,
) {
	const fileInputId = useId()
	const {notification} = useNotification()
	const [agentEmail, agentName] = useAgent()
	const [selectedFormType, setSelectedFormType] = useState<string>("")
	const [fieldValues, setFieldValues] = useState<Record<string, FieldValue>>({})
	const [submitting, setSubmitting] = useState(false)
	const {fileUploads, uploadFiles, removeFile, clearFiles, isUploading} = useFileUpload(customerID)

	useEffect(() => {
		onUploadingChange?.(isUploading)
	}, [isUploading, onUploadingChange])

	const getUploadChipColor = (upload: FileUpload) => {
		if (upload.uploaded) return "primary"
		if (upload.error) return "secondary"
		return "default"
	}

	useEffect(() => {
		if (forms.length === 1) {
			setSelectedFormType(forms[0].Type)
		}
	}, [forms])

	const selectedForm = forms.find((f) => f.Type === selectedFormType)

	const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files
		if (!files || files.length === 0) return

		await uploadFiles(Array.from(files))
	}

	const handleFieldChange = (fieldName: string, value: FieldValue) => {
		setFieldValues((prev) => ({
			...prev,
			[fieldName]: value,
		}))
	}

	const isFormValid = () => {
		if (!selectedForm) return false
		const requiredFields = selectedForm.Fields.filter((f) => f.Required)
		return requiredFields.every((field) => hasFieldContent(field, fieldValues[field.Name]))
	}

	const hasFormContent = () => {
		if (!selectedForm) return false
		return selectedForm.Fields.some((field) => hasFieldContent(field, fieldValues[field.Name]))
	}

	const createNoteFromForm = async (): Promise<string | null> => {
		const payload = buildNotePayload()
		if (!payload) return null
		const {NoteID} = await api.customer.createNote(customerID, payload)
		resetForm()
		return NoteID
	}

	React.useImperativeHandle(ref, () => ({
		createNote: createNoteFromForm,
		hasContent: hasFormContent,
		isUploading: () => isUploading,
	}))

	const buildNotePayload = () => {
		if (!selectedForm) return null

		const allAnswers: {field: Field; answer: FieldAnswered}[] = selectedForm.Fields.map((field) => {
			const raw = fieldValues[field.Name]
			const content = raw === undefined ? defaultValueForField(field) : raw
			return {
				field,
				answer: {
					Name: field.Name,
					Type: field.Type,
					Content: content,
				},
			}
		})

		const answers = allAnswers
			.filter(({field, answer}) => hasFieldContent(field, answer.Content))
			.map(({answer}) => answer)

		const uploads: Upload[] = fileUploads
			.filter((u) => u.uploaded && u.uploadID)
			.map((u) => ({
				ID: u.uploadID,
				FileName: u.file.name,
			}))

		return {
			FormType: selectedForm.Type,
			FormVersion: selectedForm.Version,
			Answers: answers,
			Uploads: uploads,
			AgentName: agentName,
			AgentEmail: agentEmail,
			Pending: pending,
		}
	}

	const resetForm = () => {
		setFieldValues({})
		clearFiles()
		if (forms.length > 1) {
			setSelectedFormType("")
		}
	}

	const validateForm = (): string | null => {
		if (!selectedForm) return "No form selected"

		const missingFields = selectedForm.Fields.filter(
			(field) => field.Required && !hasFieldContent(field, fieldValues[field.Name]),
		)
		if (missingFields.length > 0) {
			return `Please fill in required fields: ${missingFields.map((f) => f.Name).join(", ")}`
		}

		if (isUploading) {
			return "Please wait for file uploads to complete"
		}

		return null
	}

	const handleAddNote = async () => {
		const validationError = validateForm()
		if (validationError) {
			notification.error(validationError)
			return
		}

		const payload = buildNotePayload()
		if (!payload) return

		setSubmitting(true)

		try {
			const {NoteID} = await api.customer.createNote(customerID, payload)

			if (onNoteCreated) {
				await onNoteCreated(NoteID)
			} else {
				notification.success("Note added successfully")
			}

			resetForm()

			if (onSuccess) {
				onSuccess()
			}
		} catch (error: any) {
			notification.error(error.Message || "Failed to add note")
		} finally {
			setSubmitting(false)
		}
	}

	const canSubmit = !submitting && !disabled && selectedForm && isFormValid() && !isUploading

	const smallSx =
		size === "small" ? {fontSize: "0.875rem", "& .MuiInputBase-input": {fontSize: "0.875rem"}} : undefined

	const labelFor = (field: Field) => field.Name + (field.Required ? " *" : "")
	const placeholderFor = (field: Field) =>
		disabled ? "Notes are disabled for Latitude customers" : `Enter ${field.Name.toLowerCase()}`

	const renderField = (field: Field) => {
		const rawValue = fieldValues[field.Name]

		if (isBooleanField(field)) {
			const booleanValue = typeof rawValue === "boolean" ? rawValue : false
			return (
				<FormControl disabled={disabled} size={size}>
					<FormLabel>{labelFor(field)}</FormLabel>
					<RadioGroup
						row
						value={booleanValue ? "true" : "false"}
						onChange={(e) => handleFieldChange(field.Name, e.target.value === "true")}
					>
						<FormControlLabel value="false" control={<Radio size={size} />} label="No" />
						<FormControlLabel value="true" control={<Radio size={size} />} label="Yes" />
					</RadioGroup>
				</FormControl>
			)
		}

		if (isSelectField(field)) {
			return (
				<FormControl variant="outlined" fullWidth disabled={disabled} size={size}>
					<InputLabel>{labelFor(field)}</InputLabel>
					<Select
						value={typeof rawValue === "string" ? rawValue : ""}
						label={labelFor(field)}
						onChange={(e) => handleFieldChange(field.Name, e.target.value as string)}
					>
						<MenuItem value="">
							<em>{placeholderFor(field)}</em>
						</MenuItem>
						{(field.Options ?? []).map((option) => (
							<MenuItem key={option} value={option}>
								{option}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			)
		}

		if (isDateField(field)) {
			return (
				<TextField
					label={labelFor(field)}
					type="date"
					variant="outlined"
					fullWidth
					size={size}
					sx={smallSx}
					value={typeof rawValue === "string" ? rawValue : ""}
					onChange={(e) => handleFieldChange(field.Name, e.target.value)}
					slotProps={{inputLabel: {shrink: true}}}
					disabled={disabled}
				/>
			)
		}

		if (isNumberField(field)) {
			return (
				<TextField
					label={labelFor(field)}
					type="number"
					placeholder={placeholderFor(field)}
					variant="outlined"
					fullWidth
					size={size}
					sx={smallSx}
					value={typeof rawValue === "number" ? rawValue : rawValue === undefined ? "" : String(rawValue)}
					onChange={(e) => handleFieldChange(field.Name, parseFloat(e.target.value) || 0)}
					disabled={disabled}
				/>
			)
		}

		if (isURLField(field)) {
			return (
				<TextField
					label={labelFor(field)}
					type="url"
					placeholder="https://example.com"
					variant="outlined"
					fullWidth
					size={size}
					sx={smallSx}
					value={typeof rawValue === "string" ? rawValue : ""}
					onChange={(e) => handleFieldChange(field.Name, e.target.value)}
					disabled={disabled}
					slotProps={{htmlInput: {maxLength: 300, pattern: "https?://.+"}}}
				/>
			)
		}

		const multiline = isTextareaField(field)
		return (
			<TextField
				label={labelFor(field)}
				multiline={multiline}
				minRows={multiline ? 4 : undefined}
				maxRows={multiline ? 50 : undefined}
				type="text"
				placeholder={placeholderFor(field)}
				variant="outlined"
				fullWidth
				size={size}
				sx={smallSx}
				value={typeof rawValue === "string" ? rawValue : ""}
				onChange={(e) => handleFieldChange(field.Name, e.target.value)}
				disabled={disabled}
			/>
		)
	}

	return (
		<Grid direction="column" spacing={3} container>
			{/* Form Type Selector (only show if multiple forms) */}
			{forms.length > 1 && (
				<Grid>
					<FormControl variant="outlined" fullWidth disabled={disabled} size={size}>
						<InputLabel>Form Type</InputLabel>
						<Select
							value={selectedFormType}
							onChange={(e) => {
								setSelectedFormType(e.target.value as string)
								setFieldValues({})
							}}
							label="Form Type"
						>
							<MenuItem value="">
								<em>Select a form type</em>
							</MenuItem>
							{forms.map((form) => (
								<MenuItem key={form.Type} value={form.Type}>
									{form.Label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			)}

			{/* Dynamic Form Fields */}
			{selectedForm && selectedForm.Fields.map((field) => <Grid key={field.Name}>{renderField(field)}</Grid>)}

			{/* File Upload */}
			{selectedForm && attachmentsEnabled && (
				<Grid>
					<Box display="none">
						<input
							accept="*/*"
							id={fileInputId}
							multiple
							type="file"
							onChange={handleFileSelect}
							disabled={disabled}
						/>
					</Box>
					<label htmlFor={fileInputId}>
						<Button
							variant="outlined"
							component="span"
							size={size}
							startIcon={<AttachFileIcon />}
							disabled={disabled}
							sx={{textTransform: "none"}}
						>
							{attachButtonLabel}
						</Button>
					</label>

					{fileUploads.length > 0 && (
						<Box mt={2}>
							<Grid container spacing={1}>
								{fileUploads.map((upload, idx) => (
									<Grid key={idx}>
										<Chip
											label={upload.file.name}
											onDelete={upload.uploading ? undefined : () => removeFile(upload.file)}
											deleteIcon={
												upload.uploading ? <CircularProgress size={16} /> : <CloseIcon />
											}
											color={getUploadChipColor(upload)}
											icon={
												upload.uploading ? <CircularProgress size={16} /> : <AttachFileIcon />
											}
										/>
									</Grid>
								))}
							</Grid>
						</Box>
					)}
				</Grid>
			)}

			{/* Submit Button */}
			{showSubmitButton && (
				<Grid>
					<Grid container justifyContent="flex-end" spacing={2}>
						<Grid>
							<Button
								disabled={!canSubmit}
								onClick={handleAddNote}
								size={size}
								sx={{textTransform: "none"}}
							>
								{submitting ? (
									<CircularProgress size={size === "small" ? 16 : 24} />
								) : (
									submitButtonLabel
								)}
							</Button>
						</Grid>
					</Grid>
				</Grid>
			)}
		</Grid>
	)
})

export {NoteForm}
export type {NoteFormHandle, Form, Field, FieldAnswered, Upload}
